import { useIsomorphicLayoutEffect } from "@react-hookz/web";
import type { MutableRefObject } from "react";
import { useState } from "react";

export type PercentageReached = undefined | 0 | 20 | 50 | 100;

// TODO: refactor typeform to make use of these state
export const usePercentageIntersect = (itemElementReference: MutableRefObject<Element | null>) => {
  const [percentageReached, setPercentageReached] = useState<PercentageReached>();

  useIsomorphicLayoutEffect(() => {
    const getPercentageObserverByTargetPercentage = (targetPercentageReached: PercentageReached) =>
      new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            setPercentageReached(percentageReached => {
              if (
                percentageReached &&
                targetPercentageReached &&
                percentageReached >= targetPercentageReached
              )
                return percentageReached;
              return entry.isIntersecting ? targetPercentageReached : percentageReached;
            });
          });
        },
        { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1] },
      );

    const zeroPercentageObserver = getPercentageObserverByTargetPercentage(0);
    const twentyPercentageObserver = getPercentageObserverByTargetPercentage(20);
    const fiftyPercentageObserver = getPercentageObserverByTargetPercentage(50);
    const hundredPercentageObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setPercentageReached(percentageReached =>
            entry.isIntersecting ? 100 : percentageReached,
          );
        });
      },
      { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1] },
    );

    const zeroPercentageTarget = itemElementReference.current?.querySelector(
      ".js-reading-0-percent-completion-tracker",
    );

    const twentyPercentageTarget = itemElementReference.current?.querySelector(
      ".js-reading-20-percent-completion-tracker",
    );

    const fiftyPercentageTarget = itemElementReference.current?.querySelector(
      ".js-reading-50-percent-completion-tracker",
    );

    const hundredPercentageTarget = itemElementReference.current?.querySelector(
      ".js-reading-100-percent-completion-tracker",
    );

    zeroPercentageTarget && zeroPercentageObserver.observe(zeroPercentageTarget);
    twentyPercentageTarget && twentyPercentageObserver.observe(twentyPercentageTarget);
    fiftyPercentageTarget && fiftyPercentageObserver.observe(fiftyPercentageTarget);
    hundredPercentageTarget && hundredPercentageObserver.observe(hundredPercentageTarget);

    return () => {
      zeroPercentageObserver.disconnect();
      twentyPercentageObserver.disconnect();
      fiftyPercentageObserver.disconnect();
      hundredPercentageObserver.disconnect();
    };
  }, [itemElementReference]);

  return { percentageReached, setPercentageReached };
};
